import { OnboardingAddEmployeeDto, ResponseErrorDto, OnboardingStep } from '@interfaces/models';
import { EmployeeFormType, useEmployeeForm } from '@pages/onboarding/hooks/use-employee-form';
import { formatPhoneNumberToDisplay, sanitizePhoneNumber } from '@helpers/utils';
import { getEmployeesData, saveEmployees } from '@api/onboarding';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@shared/hooks';
import { useEffect, useState } from 'react';
import { Routes } from '@shared/enums';
import { toast } from '@ui/components';

export const useOnboardingEmployees = () => {
  const [isEmployeesFormVisible, setIsEmployeesFormVisible] = useState(true);
  const [savedEmployees, setSavedEmployees] = useState<OnboardingAddEmployeeDto[]>([]);

  const { form, defaultEmployeeValues } = useEmployeeForm();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const checkIfPhoneNumberIsUnique = (phoneNumber: string, originalPhoneNumber?: string) => {
    // When user is editing employee, we want filter out current edited employee,
    // otherwise there always will be error caused by duplicated phone number
    const employeesWithoutCurrentEditedEmployee = savedEmployees.filter(
      (employee) => employee.phoneNumber !== originalPhoneNumber,
    );

    const isUnique = employeesWithoutCurrentEditedEmployee.every(
      (employee) => employee.phoneNumber !== phoneNumber,
    );
    return isUnique;
  };

  const handleSubmit = (data: EmployeeFormType) => {
    setSavedEmployees([...savedEmployees, data]);
    form.reset(defaultEmployeeValues);
  };

  const handleEditEmployee = (employee: EmployeeFormType, idx: number) => {
    const updatedEmployees = [...savedEmployees];
    updatedEmployees[idx] = employee;
    setSavedEmployees(updatedEmployees);
  };

  const handleRemoveEmployee = (idx: number) => {
    const updatedEmployees = [...savedEmployees];
    updatedEmployees.splice(idx, 1);
    setSavedEmployees(updatedEmployees);
  };

  const addNextEmployee = () => {
    const isUnique = checkIfPhoneNumberIsUnique(form.getValues('phoneNumber'));

    if (!isUnique) {
      return form.setError('phoneNumber', {
        message: t('ONBOARDING.ADD_EMPLOYEES.PHONE_NUMBER_NOT_UNIQUE'),
      });
    }

    // submit form only if form is visible. We don't wont to trigger form when user has no employees
    if (isEmployeesFormVisible) {
      return form.handleSubmit(handleSubmit)();
    }

    setIsEmployeesFormVisible(true);
  };

  const { data: vehiclesData, isLoading: isAvailableVehiclesLoading } = useQuery({
    queryKey: ['onboarding', 'vehicles'],
    queryFn: getEmployeesData,
  });

  const saveEmployeesMutation = useMutation({
    mutationFn: (employees: OnboardingAddEmployeeDto[]) => {
      return saveEmployees(
        employees.map((employee) => ({
          ...employee,
          phoneNumber: sanitizePhoneNumber(employee.phoneNumber),
        })),
        OnboardingStep.TOP_UP_BALANCE,
      );
    },
    onSuccess: () => {
      navigate(`../${Routes.TOP_UP_BALANCE}`);
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const closeEmployeesForm = () => {
    setIsEmployeesFormVisible(false);
    form.reset();
  };

  const handleCopyUserRegistrationData = () => {
    form.reset({
      phoneNumber: formatPhoneNumberToDisplay(
        user?.phoneNumber ?? defaultEmployeeValues.phoneNumber,
      ),
      firstName: user?.firstName ?? defaultEmployeeValues.firstName,
      phoneExt: user?.phoneExt ?? defaultEmployeeValues.phoneExt,
      lastName: user?.lastName ?? defaultEmployeeValues.lastName,
      email: user?.email ?? defaultEmployeeValues.email,
    });
  };

  const handleSaveAndContinue = async () => {
    // Check if phone number is unique
    if (!checkIfPhoneNumberIsUnique(form.getValues('phoneNumber'))) {
      form.setError('phoneNumber', {
        message: t('ONBOARDING.ADD_EMPLOYEES.PHONE_NUMBER_NOT_UNIQUE'),
      });
      return;
    }

    // Check if form has any errors
    if (isEmployeesFormVisible) {
      await form.trigger();
      const hasAnyFormErrors = Object.keys(form.formState.errors).length > 0;
      if (hasAnyFormErrors) return;
    }

    // Some employees are not saved to state yet, so we need to add them to the employees array
    // but only if form is visible
    const allEmployees = [...savedEmployees, ...(isEmployeesFormVisible ? [form.getValues()] : [])];
    saveEmployeesMutation.mutate(allEmployees);
  };

  useEffect(() => {
    if (!savedEmployees.length) {
      setIsEmployeesFormVisible(true);
    }
  }, [savedEmployees]);

  return {
    isSaveMutationPending: saveEmployeesMutation.isPending,
    availableVehicles: vehiclesData?.vehicles,
    isAvailableVehiclesLoading,
    isEmployeesFormVisible,
    savedEmployees,
    form,
    handleCopyUserRegistrationData,
    checkIfPhoneNumberIsUnique,
    handleSaveAndContinue,
    handleRemoveEmployee,
    closeEmployeesForm,
    handleEditEmployee,
    addNextEmployee,
    handleSubmit,
  };
};
